import * as t from 'io-ts';

import {makeDefaultValueType, makeEnumRuntimeType, RLazyTeammateIdRef} from './apiTypes';
import {parseContact, RContact} from './contactModel';
import {RRuleBundleType} from './ruleBundleModel';

/*
 * Runtime types.
 */

export enum ActivitySourceTypesEnum {
  ASANA = 'asana',
  COMPANY = 'company',
  CONVERSATION_SIZE = 'conversation_size',
  DATA_RETENTION = 'data_retention',
  DATA_LEAK = 'data_leak',
  FACEBOOK = 'facebook',
  GITHUB = 'github',
  GMAIL = 'gmail',
  INACTIVE = 'inactive',
  INTERCOM = 'intercom',
  JIRA = 'jira',
  JOIN = 'join',
  LAYER = 'layer',
  MESSAGE_SEEN = 'message_seen',
  OFFICE365 = 'office365',
  PUBLIC_API = 'public_api',
  REMINDER = 'reminder',
  RULE = 'rule',
  SCRIPT = 'script',
  SENDING = 'sending',
  SLACK = 'slack',
  SMTP = 'smtp',
  SPAM_FILTER = 'spam_filter',
  TALKDESK = 'talkdesk',
  TEAMMATE = 'teammate',
  TEAMMATE_DELETED = 'teammate_deleted',
  TRULY = 'truly',
  TWILIO = 'twilio',
  TWITTER = 'twitter',
  UNAVAILABLE = 'unavailable',
  VACATION_RESPONDER = 'vacation_responder',
  ZAPIER = 'zapier',
  OTHER = 'other',
}

export enum ActivitySourceVisualTypesEnum {
  TEAMMATE = 'TEAMMATE',
  CONTACT = 'CONTACT',
  RULE = 'RULE',
  AUTOMATED_REPLY = 'AUTOMATED_REPLY',
  OUT_OF_OFFICE_REPLY = 'OUT_OF_OFFICE_REPLY',
  OTHER = 'OTHER',
}

const RBaseActivitySource = t.type({
  type: makeDefaultValueType(makeEnumRuntimeType(ActivitySourceTypesEnum), ActivitySourceTypesEnum.OTHER),
});

const RActivitySourceTeammate = t.intersection([
  RBaseActivitySource,
  t.type({
    visualType: t.literal(ActivitySourceVisualTypesEnum.TEAMMATE),
    teammateId: RLazyTeammateIdRef,
    wasUnavailable: t.boolean,
    wasDeleted: makeDefaultValueType(t.boolean, false),
  }),
]);

const RActivitySourceRule = t.intersection([
  RBaseActivitySource,
  t.type({
    visualType: t.literal(ActivitySourceVisualTypesEnum.RULE),
    ruleId: t.Integer,
    ruleName: t.string,
    ruleNamespace: t.union([t.string, t.undefined]),
    ruleBundleId: t.union([t.Integer, t.undefined]),
    ruleBundleType: t.union([RRuleBundleType, t.undefined]),
  }),
]);

const RActivitySourceAutomatedReply = t.intersection([
  RBaseActivitySource,
  t.type({
    visualType: t.literal(ActivitySourceVisualTypesEnum.AUTOMATED_REPLY),
  }),
]);

const RActivitySourceOutOfOfficeReply = t.intersection([
  RBaseActivitySource,
  t.type({
    visualType: t.literal(ActivitySourceVisualTypesEnum.OUT_OF_OFFICE_REPLY),
  }),
]);

const RActivitySourceContact = t.intersection([
  RBaseActivitySource,
  t.type({
    visualType: t.literal(ActivitySourceVisualTypesEnum.CONTACT),
    contact: RContact,
    name: t.string,
    senderChannelId: t.union([t.Integer, t.undefined]),
  }),
]);

const RActivitySourceOther = t.intersection([
  RBaseActivitySource,
  t.type({
    visualType: t.literal(ActivitySourceVisualTypesEnum.OTHER),
    name: t.union([t.string, t.undefined]),
    ruleWasDeleted: t.union([t.boolean, t.undefined]),
  }),
]);

const RActivitySourceDirty = t.readonly(
  t.taggedUnion('visualType', [
    RActivitySourceTeammate,
    RActivitySourceRule,
    RActivitySourceContact,
    RActivitySourceOther,
  ]),
);

const RMessageActivitySourceDirty = t.readonly(
  t.taggedUnion('visualType', [RActivitySourceAutomatedReply, RActivitySourceOutOfOfficeReply]),
);

/*
 * Types.
 */

export interface ActivitySourceTeammateModel extends t.TypeOf<typeof RActivitySourceTeammate> {}
export interface ActivitySourceRuleModel extends t.TypeOf<typeof RActivitySourceRule> {}
export interface ActivitySourceAutomatedReplyModel extends t.TypeOf<typeof RActivitySourceAutomatedReply> {}
export interface ActivitySourceOutOfOfficeReplyModel
  extends t.TypeOf<typeof RActivitySourceOutOfOfficeReply> {}
export interface ActivitySourceOtherModel extends t.TypeOf<typeof RActivitySourceOther> {}
export interface ActivitySourceContactModel extends t.TypeOf<typeof RActivitySourceContact> {}
export interface ActivitySourceOtherModel extends t.TypeOf<typeof RActivitySourceOther> {}
export type ActivitySourceModel =
  | ActivitySourceTeammateModel
  | ActivitySourceRuleModel
  | ActivitySourceContactModel
  | ActivitySourceOtherModel;
export type MessageActivitySourceModel =
  | ActivitySourceAutomatedReplyModel
  | ActivitySourceOutOfOfficeReplyModel;

export const RActivitySource = t.clean<ActivitySourceModel>(RActivitySourceDirty as any);
export const RMessageActivitySource = t.clean<MessageActivitySourceModel>(RMessageActivitySourceDirty as any);

/*
 * IO methods.
 */

export function parseActivitySource(src: any): ActivitySourceModel {
  const type = parseActivitySourceType(src.source);
  const visualType = findNonMessageActivitySourceVisualType(src, type);

  switch (visualType) {
    case ActivitySourceVisualTypesEnum.TEAMMATE:
      return {
        type,
        visualType: ActivitySourceVisualTypesEnum.TEAMMATE,
        teammateId:
          (src.from && src.from.id) || (src.involved && src.involved[0] && src.involved[0].teammate_id),
        wasUnavailable:
          src.source === ActivitySourceTypesEnum.SENDING ||
          src.source === ActivitySourceTypesEnum.UNAVAILABLE,
        wasDeleted: src.source === ActivitySourceTypesEnum.TEAMMATE_DELETED,
      };

    case ActivitySourceVisualTypesEnum.RULE:
      return {
        type,
        visualType: ActivitySourceVisualTypesEnum.RULE,
        ruleId: src.rule_id,
        ruleName: src.rule_name,
        ruleNamespace: src.rule_namespace,
        ruleBundleId: src.rule_bundle_id,
        ruleBundleType: RRuleBundleType.is(src.rule_bundle_type) ? src.rule_bundle_type : undefined,
      };

    case ActivitySourceVisualTypesEnum.CONTACT:
      return {
        type,
        visualType: ActivitySourceVisualTypesEnum.CONTACT,
        contact: parseContact(src.contact),
        name: src.contact.display_name,
        senderChannelId:
          (src.from && src.from.class && src.from.class === 'inbox' && src.from.channel_id) || undefined,
      };

    default:
      return {
        type,
        visualType: ActivitySourceVisualTypesEnum.OTHER,
        name: src.source_name,
        ruleWasDeleted: src.rule_was_deleted || false,
      };
  }
}

export function parseMessageActivitySource(
  src: any,
): ActivitySourceOutOfOfficeReplyModel | ActivitySourceAutomatedReplyModel | undefined {
  const type = parseActivitySourceType(src.source);
  const visualType = findMessageActivitySourceVisualType(type);

  switch (visualType) {
    case ActivitySourceVisualTypesEnum.OUT_OF_OFFICE_REPLY:
      return {
        type,
        visualType: ActivitySourceVisualTypesEnum.OUT_OF_OFFICE_REPLY,
      };

    case ActivitySourceVisualTypesEnum.AUTOMATED_REPLY:
      return {
        type,
        visualType: ActivitySourceVisualTypesEnum.AUTOMATED_REPLY,
      };

    default:
      return undefined;
  }
}

function parseActivitySourceType(activitySource: any) {
  switch (activitySource) {
    case 'teammate':
    case 'reply_comment':
    case 'unavailable':
    case 'front':
    case 'teammate_deleted':
      return ActivitySourceTypesEnum.TEAMMATE;

    case 'gmail':
    case 'imap':
      return ActivitySourceTypesEnum.GMAIL;

    default:
      return activitySource;
  }
}

const contactSourceTypes = [ActivitySourceTypesEnum.MESSAGE_SEEN, ActivitySourceTypesEnum.SENDING];

function findNonMessageActivitySourceVisualType(src: any, type: any) {
  if (type === ActivitySourceTypesEnum.TEAMMATE && src.from && src.from.id) {
    return ActivitySourceVisualTypesEnum.TEAMMATE;
  }

  if (type === ActivitySourceTypesEnum.TEAMMATE && src.involved && src.involved[0].teammate_id) {
    return ActivitySourceVisualTypesEnum.TEAMMATE;
  }

  if (type === ActivitySourceTypesEnum.RULE && src.rule_id) {
    return ActivitySourceVisualTypesEnum.RULE;
  }

  const isContactSource = contactSourceTypes.includes(type);
  const hasContact = src.contact && src.contact.class;
  if (isContactSource && hasContact) {
    return ActivitySourceVisualTypesEnum.CONTACT;
  }

  return ActivitySourceVisualTypesEnum.OTHER;
}

function findMessageActivitySourceVisualType(type: any) {
  if (type === ActivitySourceTypesEnum.VACATION_RESPONDER) {
    return ActivitySourceVisualTypesEnum.OUT_OF_OFFICE_REPLY;
  }

  if (type === ActivitySourceTypesEnum.RULE) {
    return ActivitySourceVisualTypesEnum.AUTOMATED_REPLY;
  }

  return ActivitySourceVisualTypesEnum.OTHER;
}

/*
 * Helpers.
 */

export function isTeammateActivitySource(source: ActivitySourceModel): source is ActivitySourceTeammateModel {
  return source.visualType === ActivitySourceVisualTypesEnum.TEAMMATE;
}
